import { ErrorBoundaryContext } from "@prismicio/editor-support/Suspense"
import clsx from "clsx"
import { useContext, useRef } from "react"
import { RemoveScroll } from "react-remove-scroll"

import { Badge } from "../Badge"
import { Button } from "../Button"
import { Icon } from "../Icon"
import { ScrollArea } from "../ScrollArea"
import { Skeleton } from "../Skeleton"
import { Text } from "../Text"
import styles from "./TagField.module.css"
import { TagFieldHeader } from "./TagFieldHeader"
import { useTagField } from "./useTagField"

export interface TagFieldProps {
  open: boolean
  setOpen: (open: boolean) => void
  values?: Set<string>
  selectedValues: Set<string>
  onSelectedValuesChange: (selectedValues: Set<string>) => void
  error?: boolean
  loading?: boolean
}

export function TagField(props: TagFieldProps) {
  const { open, values = new Set([]), error, loading } = props

  const fieldRef = useRef<HTMLDivElement>(null)

  const { searchText, setSearchText, onCreateTag, addItemToSelectedValues } =
    useTagField({
      ...props,
      fieldRef,
    })

  const openState = open ? "open" : "closed"

  return (
    <div className={styles.tagField}>
      <Overlay openState={openState} />
      <div
        role="button"
        className={clsx(styles[`overlayMode-${openState}`])}
        ref={fieldRef}
      >
        <TagFieldHeader
          {...props}
          isDisabled={loading || error}
          searchText={searchText}
          fieldRef={fieldRef}
          setSearchText={setSearchText}
          onCreateTag={onCreateTag}
        />
        {open && (
          <TagFieldFooter
            {...props}
            values={values}
            searchText={searchText}
            addItemToSelectedValues={addItemToSelectedValues}
            onCreateTag={onCreateTag}
          />
        )}
      </div>
    </div>
  )
}

interface TagFooterProps {
  values: Set<string>
  selectedValues: Set<string>
  addItemToSelectedValues: (value: string) => void
  searchText: string
  onCreateTag: () => void
  loading?: boolean
  error?: boolean
}

function TagFieldFooter(props: TagFooterProps) {
  return (
    <RemoveScroll>
      <div className={styles.footer}>
        <div className={styles.footerTitle}>
          <Text color="grey11" variant="smallBold">
            Select an existing tag or create a new one
          </Text>
        </div>
        <TagFieldFooterContent {...props} />
      </div>
    </RemoveScroll>
  )
}

function TagFieldFooterContent(props: TagFooterProps) {
  const {
    values,
    selectedValues,
    addItemToSelectedValues,
    searchText,
    onCreateTag,
  } = props

  if (props.loading) return <TagsSkeleton />
  if (props.error) return <TagsError />
  return (
    <>
      <ScrollArea>
        <div className={styles.optionsContainer}>
          {tagOptions(values, new Set([...selectedValues]), searchText).map(
            (option) => (
              <div key={option}>
                <Badge
                  title={option}
                  key={option}
                  color="purple"
                  onClick={() => addItemToSelectedValues(option)}
                  size="medium"
                />
              </div>
            ),
          )}
        </div>
      </ScrollArea>
      {searchText !== "" && (
        <button className={styles.footerButton} onClick={onCreateTag}>
          <Icon name="add" color="grey11" size="small" />
          <Text color="grey12">{`Create "${searchText}"`}</Text>
        </button>
      )}
    </>
  )
}

function TagsError() {
  const context = useContext(ErrorBoundaryContext)
  return (
    <div className={styles.tagErrorContainer}>
      <div className={styles.tagErrorLeft}>
        <Icon name="alert" size="large" color="tomato11" />
        <Text variant="normal" color="tomato11">
          Failed to load the tags.
        </Text>
      </div>
      {context && (
        <Button
          endIcon="refresh"
          color="tomato"
          onClick={context?.reset}
          invisible
        >
          Retry
        </Button>
      )}
    </div>
  )
}

function TagsSkeleton() {
  return (
    <div className={styles.optionsContainer}>
      <div className={styles.tagsSkeleton}>
        <Skeleton height="100%" width="100%" />
      </div>
    </div>
  )
}

interface OverlayProps {
  openState: "open" | "closed"
}

function Overlay(props: OverlayProps) {
  const { openState } = props
  return <div className={styles[`overlay-${openState}`]} />
}

function tagOptions(
  values: Set<string>,
  selectedValues: Set<string>,
  searchTerm: string,
) {
  function optionIsNotSelected(option: string) {
    return !selectedValues.has(option)
  }

  function optionMatchTextSearch(option: string, searchTerm: string) {
    return option.toLowerCase().includes(searchTerm.toLowerCase())
  }

  return Array.from(values).filter(
    (option) =>
      optionIsNotSelected(option) && optionMatchTextSearch(option, searchTerm),
  )
}
